import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import type * as Y from 'yjs';

import type { PDF } from '../entities/pdf';
import type { PageTextLayer } from '../renderer';
import { PdfEditStore } from './edit-store';
import type { PdfEdit } from './types';

export function usePdfEditStore(model: AttachmentBlockModel): PdfEditStore {
  return useMemo(() => new PdfEditStore(model), [model]);
}

export function usePdfPageEdits(
  store: PdfEditStore,
  page: number
): PdfEdit[] {
  const subscribe = useCallback(
    (onChange: () => void) => store.subscribe(onChange),
    [store]
  );
  const version = useSyncExternalStore(subscribe, () => store.getVersion());

  return useMemo(
    () => store.getEditsForPage(page),
    // version invalidates the memo whenever the underlying Y data changes
     
    [store, page, version]
  );
}

/**
 * Text geometry of a page. Pass `enabled: false` to skip extraction
 * (e.g. while the viewer is not in an edit mode).
 */
export function usePdfTextLayer(
  pdf: PDF,
  pageNum: number,
  enabled: boolean
): PageTextLayer | null {
  const [textLayer, setTextLayer] = useState<PageTextLayer | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const subscription = pdf.textLayer$(pageNum).subscribe({
      next: layer => setTextLayer(layer),
      error: error => console.error('Failed to extract PDF text layer', error),
    });
    return () => subscription.unsubscribe();
  }, [pdf, pageNum, enabled]);

  return textLayer;
}

/**
 * Two-way binding to a Y.Text. Local input is applied as a minimal
 * prefix/suffix diff so concurrent edits from collaborators merge at
 * character granularity instead of overwriting each other.
 */
export function useYText(yText: Y.Text): [string, (next: string) => void] {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const observer = () => onChange();
      yText.observe(observer);
      return () => yText.unobserve(observer);
    },
    [yText]
  );
  const value = useSyncExternalStore(subscribe, () => yText.toString());

  const setValue = useCallback(
    (next: string) => {
      const current = yText.toString();
      if (next === current) return;

      let start = 0;
      while (
        start < current.length &&
        start < next.length &&
        current[start] === next[start]
      ) {
        start++;
      }
      let currentEnd = current.length;
      let nextEnd = next.length;
      while (
        currentEnd > start &&
        nextEnd > start &&
        current[currentEnd - 1] === next[nextEnd - 1]
      ) {
        currentEnd--;
        nextEnd--;
      }

      const apply = () => {
        if (currentEnd > start) yText.delete(start, currentEnd - start);
        if (nextEnd > start) yText.insert(start, next.slice(start, nextEnd));
      };
      if (yText.doc) {
        yText.doc.transact(apply);
      } else {
        apply();
      }
    },
    [yText]
  );

  return [value, setValue];
}
